import type { PayloadAction } from '@reduxjs/toolkit';

import type { SagaIterator } from 'redux-saga';
import { call, delay, put, select } from 'redux-saga/effects';
import i18n from '../../i18n';
import {
  type BillingForm,
  type BillingState,
  clearError,
  setCountryState,
  setError,
  setForm,
} from '../../store/billingSlice';
import {
  setEmailValidation,
  setEmailValidationError,
} from '../../store/checkoutSlice';
import type { RootState } from '../../store/store';
import type { EmailValidation } from '../../types/emailValidation';
import api from '../api';
import { billingFieldValidators } from './constants';
import { isEUCountry, validateAddressCountries } from './countries';
import { notEmpty, present } from './utils';

export function* validateBillingField(
  action: PayloadAction<Record<string, string>>,
): Generator<unknown, void, unknown> {
  const field = Object.keys(action.payload)[0];
  if (field === 'email') {
    return;
  }
  const value = action.payload[field];
  const { validators } =
    billingFieldValidators[field as keyof typeof billingFieldValidators];
  let error: string | undefined;
  for (const { func, message } of validators) {
    const valid = yield call(func, value);
    if (!valid) {
      error = message;
      break;
    }
  }

  if (error != null) {
    yield put(setError({ [field]: error }));
  } else {
    yield put(clearError(field));
  }
}

function mandatoryBillingFields(): (keyof BillingForm)[] {
  return Object.entries(billingFieldValidators)
    .filter(([, { mandatory }]) => mandatory)
    .map(([field]) => field as keyof BillingForm);
}

export function isValid(
  form: BillingForm,
  errors: Record<string, string | null>,
): boolean {
  const allMandatoryFilled = mandatoryBillingFields().every((field) =>
    notEmpty(form[field]),
  );
  const hasError = Object.values(errors).some((error) => error != null);

  return allMandatoryFilled && !hasError;
}

export function* validateCountry(value: string): SagaIterator<boolean> {
  const { form } = (yield select(
    (state: RootState) => state.billing,
  )) as BillingState;
  if (value && validateAddressCountries.includes(value)) {
    yield call(validateBillingField, {
      type: '',
      payload: { address: form.address || '' },
    });
  }

  return present(value);
}

export function* validateAddress(value: string): SagaIterator<boolean> {
  const { form } = yield select((state) => state.billing);
  yield delay(1000);
  if (form.country && validateAddressCountries.includes(form.country)) {
    const valid = yield call(checkAddress, value);
    if (valid) {
      yield put(clearError('zip'));
    } else {
      yield put(setError({ zip: '' }));
    }
    return valid;
  }

  return present(value);
}

export function* validateZip(value: string): SagaIterator<boolean> {
  const { form } = yield select((state) => state.billing);
  yield delay(1000);
  if (form.country && validateAddressCountries.includes(form.country)) {
    const valid = yield call(checkAddress, form.address);
    if (valid) {
      yield put(clearError('address'));
    } else {
      yield put(setError({ address: '' }));
    }
    return valid;
  }

  return present(value);
}

export function* validateVat(value: string): SagaIterator<boolean> {
  const { form } = yield select((state) => state.billing);

  if (notEmpty(value) && isEUCountry(form.country)) {
    const result = yield call(checkVat, value);
    if (result.isValid) {
      if (!form.company) {
        yield put(setForm({ company: result.companyName }));
      }
    }
    return result.isValid;
  }

  return true;
}

function* checkVat(value: string): SagaIterator<boolean> {
  try {
    const result = yield call(api.fetch, `${window.ORDERS}/validate/vat`, {
      method: 'POST',
      body: JSON.stringify({ vatNumber: value }),
      headers: {
        'Content-Type': 'application/json',
      },
      throwError: true,
    });
    return result;
  } catch (_) {
    return false;
  }
}

function* checkAddress(value: string): SagaIterator<boolean> {
  const { form } = yield select((state) => state.billing);

  try {
    const data = {
      zipCode: form.zip || '',
      city: form.city || '',
      country: form.country,
      address: value,
    };
    const result = yield call(api.fetch, `${window.ORDERS}/validate/address`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      throwError: true,
    });

    yield put(setCountryState(result.region || ''));
    return result.isAddressVerified;
  } catch (_) {
    return false;
  }
}

export function* validateEmail(
  email: string,
): SagaIterator<EmailValidation | null> {
  return (yield call(api.fetch, `${window.ORDERS}/cart/precheck`, {
    method: 'POST',
    body: JSON.stringify({ email }),
    headers: {
      'Content-Type': 'application/json',
    },
  })) as EmailValidation | null;
}

export function* handleValidateEmail(action: {
  type: string;
  value: string;
}): SagaIterator<void> {
  yield put(setEmailValidationError(null));
  yield put(setEmailValidation(null));

  const { value } = action;
  // An empty email comes back `invalid`; don't flag the field while it's blank.
  if (value === '') {
    return;
  }

  yield delay(500);

  const validationData = yield call(validateEmail, value);

  if (validationData?.status === 'ok') {
    yield put(
      setEmailValidation(validationData.canContinue ? 'new' : 'existing'),
    );
  }
  if (validationData?.status === 'invalid') {
    yield put(setEmailValidation('error'));
    yield put(
      setEmailValidationError(i18n.t('Please enter a valid email address.')),
    );
  }
  if (validationData?.status === 'blocked') {
    yield put(setEmailValidation('error'));
    yield put(
      setEmailValidationError(
        i18n.t(
          'Please contact the Chaos License Compliance Team for assistance.',
        ),
      ),
    );
  }
}
